/**
 * Do Not Contact (DNC) helpers — shared invariant logic for the 4-category model.
 *
 * Model:
 *   - dnc       -> master "Everything" flag (crm_contacts.do_not_contact)
 *   - dncCall   -> phone/calls (crm_contacts.dnc_call)
 *   - dncEmail  -> emails       (crm_contacts.dnc_email)
 *   - dncSms    -> texts/SMS    (crm_contacts.dnc_sms)
 *
 * Invariants kept consistent here AND by a DB trigger:
 *   - Toggling "Everything" sets/clears all three channels.
 *   - "Everything" is true iff all three channels are true.
 */

/**
 * Apply a toggle change to the current DNC state and return the resulting
 * camelCase state. `change` is one of:
 *   { everything: boolean } | { call: boolean } | { email: boolean } | { sms: boolean }
 *
 * @param {{dncCall?:boolean,dncEmail?:boolean,dncSms?:boolean}} current
 * @param {object} change
 * @returns {{dnc:boolean,dncCall:boolean,dncEmail:boolean,dncSms:boolean}}
 */
export function computeDnc(current, change) {
  let call = !!(current && current.dncCall);
  let email = !!(current && current.dncEmail);
  let sms = !!(current && current.dncSms);

  if (change && 'everything' in change) {
    const v = !!change.everything;
    call = v;
    email = v;
    sms = v;
  } else if (change) {
    if ('call' in change) call = !!change.call;
    if ('email' in change) email = !!change.email;
    if ('sms' in change) sms = !!change.sms;
  }

  const everything = call && email && sms;
  return { dnc: everything, dncCall: call, dncEmail: email, dncSms: sms };
}

/**
 * Map a camelCase DNC state to the snake_case crm_contacts column update object.
 * @param {{dnc:boolean,dncCall:boolean,dncEmail:boolean,dncSms:boolean}} camel
 */
export function toDncColumns(camel) {
  return {
    do_not_contact: !!camel.dnc,
    dnc_call: !!camel.dncCall,
    dnc_email: !!camel.dncEmail,
    dnc_sms: !!camel.dncSms,
  };
}

/**
 * Convenience: compute the snake_case crm_contacts update for a toggle change.
 * @param {{dncCall?:boolean,dncEmail?:boolean,dncSms?:boolean}} current
 * @param {object} change
 */
export function buildDncUpdate(current, change) {
  return toDncColumns(computeDnc(current, change));
}

/**
 * Effective per-channel block (master OR channel). Mirrors backend isDncBlocked.
 * @param {{dnc?:boolean,dncCall?:boolean,dncEmail?:boolean,dncSms?:boolean}} contact
 * @param {'call'|'email'|'sms'} channel
 */
export function isChannelBlocked(contact, channel) {
  if (!contact) return false;
  if (contact.dnc) return true;
  if (channel === 'call') return !!contact.dncCall;
  if (channel === 'email') return !!contact.dncEmail;
  if (channel === 'sms') return !!contact.dncSms;
  return false;
}

/** Short human summary of active DNC categories, e.g. "All", "Email, SMS", or "" */
export function dncSummary(contact) {
  if (!contact) return '';
  if (contact.dnc) return 'All';
  const parts = [];
  if (contact.dncCall) parts.push('Call');
  if (contact.dncEmail) parts.push('Email');
  if (contact.dncSms) parts.push('SMS');
  return parts.join(', ');
}
